import React, { useState } from "react";

import { ACTION_COLORS, defaultsOf, listColumns, settingsOf } from "./custom-module-utils";
import { previewModule } from "./builder-draft";
import { MODULE_TEMPLATES, templateFacts } from "./module-templates";
import { RecordForm } from "./record-fields";

const Caption = ({ children }) => <div className="fs-12 fw-600 text-ltgrey mb-1">{children}</div>;

// What a template sets up beyond the form: list columns, board, buttons, detail page.
const TemplateSummary = ({ template }) => {
  const { module } = template;
  const settings = settingsOf(module);
  const board = module.fields.find((field) => field.key === settings.list.board_field);

  return (
    <div className="p-3 mb-3 cm-card d-flex flex-wrap gap-4">
      <div>
        <Caption>LIST COLUMNS</Caption>
        <div className="d-flex flex-wrap gap-1">
          {settings.code_prefix && <span className="cm-tag">Record No.</span>}
          {listColumns(module).map((field) => <span key={field.key} className="cm-tag">{field.label}</span>)}
        </div>
      </div>
      {board && (
        <div>
          <Caption>BOARD</Caption>
          <div className="fs-13 fw-500 text-dkblue">Grouped by {board.label}</div>
        </div>
      )}
      {module.actions.length > 0 && (
        <div>
          <Caption>BUTTONS</Caption>
          <div className="d-flex flex-wrap gap-1">
            {module.actions.map((action) => (
              <span
                key={action.label}
                className="fs-12 fw-600"
                style={{ background: ACTION_COLORS[action.color]?.color, color: "#fff", borderRadius: "6px", padding: "3px 10px" }}
              >
                {action.label}
              </span>
            ))}
          </div>
        </div>
      )}
      <div>
        <Caption>DETAIL PAGE</Caption>
        <div className="fs-13 fw-500 text-dkblue">
          {[settings.view.history && "Activity & notes", settings.view.related && "Related records"].filter(Boolean).join(" · ") || "Details only"}
        </div>
      </div>
    </div>
  );
};

// The builder's Preview tab: ready-made designs to start from, then a live
// form for whichever is being looked at, the picked template or your own.
// `onUseTemplate` returns true once the template has replaced the draft.
export const PreviewTab = ({ draft, values, setValues, onUseTemplate }) => {
  const [pickedKey, setPickedKey] = useState(null);
  const [templateValues, setTemplateValues] = useState({});
  const picked = MODULE_TEMPLATES.find((template) => template.key === pickedKey);
  // An existing module has records keyed by its own fields; a template would orphan them.
  const canUse = !draft.id;

  const pick = (template) => {
    setPickedKey(template.key);
    setTemplateValues(defaultsOf(template.module));
  };

  return (
    <>
      <div className="p-4 mb-3 cm-card">
        <div className="fs-16 fw-600 text-dkblue mb-1">Start from a template</div>
        <div className="fs-13 fw-500 text-grey mb-3">
          {canUse
            ? "Pick a design to preview it below. Using it fills in every tab (fields, layout, list, detail page and buttons), and you can change anything afterwards."
            : "Templates start new modules. This module already exists, so its records stay with its own fields. Use Add Module to start from a template."}
        </div>
        <div className="cm-template-grid">
          {MODULE_TEMPLATES.map((template) => (
            <button
              key={template.key}
              type="button"
              className={`cm-template-card ${pickedKey === template.key ? "active" : ""}`}
              aria-pressed={pickedKey === template.key}
              onClick={() => pick(template)}
            >
              <div className="fs-15 fw-600 text-dkblue">{template.name}</div>
              <div className="fs-12 fw-500 text-grey mb-2">{template.description}</div>
              <div className="d-flex flex-wrap gap-1">
                {templateFacts(template).map((fact) => <span key={fact} className="cm-tag">{fact}</span>)}
              </div>
            </button>
          ))}
        </div>
      </div>

      {picked ? (
        <>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
            <div className="fs-16 fw-600 text-dkblue">Template preview: {picked.name}</div>
            <div className="d-flex gap-2">
              <button type="button" className="btn-cancel" onClick={() => setPickedKey(null)}>Back to your form</button>
              <button
                type="button"
                className="btn-save"
                disabled={!canUse}
                onClick={() => onUseTemplate(picked) && setPickedKey(null)}
              >
                Use this template
              </button>
            </div>
          </div>
          <TemplateSummary template={picked} />
          <RecordForm module={picked.module} values={templateValues} onChange={(key, value) => setTemplateValues((prev) => ({ ...prev, [key]: value }))} />
        </>
      ) : (
        <>
          <div className="fs-16 fw-600 text-dkblue mb-1">Your form</div>
          <div className="fs-13 fw-500 text-grey mb-2">
            The create form as it will look. Try it: conditional fields appear and disappear as you fill it in. Nothing here is saved.
          </div>
          <RecordForm module={previewModule(draft)} values={values} onChange={(key, value) => setValues((prev) => ({ ...prev, [key]: value }))} />
        </>
      )}
    </>
  );
};
